import UrlManager from './UrlManager'
import DebounceManager from './DebounceManager'

import * as actions from '../actions'
import Events from './Events'
import RequestSequencer from './RequestSequencer'

export const DEFAULT_STATE = {
	pageNumber: 1,
	pageSize: 12,
	orderBy: '',
	facets: [],
	category: '',
	searchTerm: '',
	wasSearched: false,
	initialCategoryId: null,
	priceRange: [],
}

export default class SearchDriver {
	state = DEFAULT_STATE

	constructor({ onSearch }) {
		this.actions = Object.entries(actions).reduce(
			(acc, [actionName, action]) => ({
				...acc,
				[actionName]: action.bind(this),
			}),
			{}, // the initial value
		)
		Object.assign(this, this.actions)

		this.events = new Events({ onSearch })

		this.subscriptions = []
		this.debounceManager = new DebounceManager()
		this.searchRequestSequencer = new RequestSequencer()
		this.updateSearchResults = this._updateSearchResults

		this.urlManager = new UrlManager()
		const urlState = this.urlManager.getStateFromUrl()

		this.urlManager.onURLStateChange(urlState => {
			this._updateSearchResults({ ...DEFAULT_STATE, ...urlState }, { skipPushToUrl: true })
		})

		this.state = {
			...this.state,
			...urlState,
		}
	}

	_updateSearchResults = (searchParameters, { skipPushToUrl = false } = {}) => {
		if (!searchParameters) return

		const {
			pageNumber,
			pageSize,
			facets,
			category,
			searchTerm,
			orderBy,
			priceRange,
			initialCategoryId,
		} = {
			...this.state,
			...searchParameters,
		}

		this.debounceManager.cancelByName('_updateSearchResults')

		this._setState({
			pageNumber,
			pageSize,
			facets,
			category,
			searchTerm,
			orderBy,
			priceRange,
			initialCategoryId,
		})

		this._makeSearchRequest({ skipPushToUrl })
	}

	_makeSearchRequest = DebounceManager.debounce(0, ({ skipPushToUrl }) => {
		const { pageNumber, pageSize, facets, category, searchTerm, orderBy, priceRange } =
			this.state
		this._setState({
			isLoading: true,
		})

		const requestId = this.searchRequestSequencer.next()

		const requestUrl = this.urlManager.getRequestUrl(this.state)

		return this.events.search(requestUrl).then(() => {
			if (this.searchRequestSequencer.isOldRequest(requestId)) return
			this.searchRequestSequencer.completed(requestId)

			this._setState({
				isLoading: false,
				wasSearched: true,
			})

			if (!skipPushToUrl) {
				this.debounceManager.runWithDebounce(
					500,
					'pushStateToURL',
					this.urlManager.pushStateToUrl.bind(this.urlManager),
					{
						pageNumber,
						pageSize,
						facets,
						searchTerm,
						category,
						orderBy,
						priceRange,
					},
				)
			}
		})
	})

	/**
	 * Any time state is updated in this Driver, the provided callback
	 * will be executed with the updated state.
	 *
	 * @param onStateChange Function
	 */
	subscribeToStateChanges(onStateChange) {
		this.subscriptions.push(onStateChange)
	}

	/**
	 * @param onStateChange Function
	 */
	unsubscribeToStateChanges(onStateChange) {
		this.subscriptions = this.subscriptions.filter(sub => sub !== onStateChange)
	}

	/**
	 * Retrieves all available acitons
	 *
	 * @returns Object All actions
	 */
	getActions() {
		return this.actions
	}

	_setState(newState) {
		const state = { ...this.state, ...newState }
		this.state = state
		this.subscriptions.forEach(subscription => subscription(state))
	}

	/**
	 * Retrieve current state. Typically used on app initialization. Subsequent
	 * state updates should come through subscription.
	 *
	 * @returns Object Current state
	 */
	getState() {
		// We return a copy of state here, because we want to ensure the state
		// inside of this object remains immutable.
		return { ...this.state }
	}
}
